import { RSiteBanner } from '@counsel-project/counsel-auth-api/dist/common/database/SiteBanner'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DashboardIcon from '@mui/icons-material/DashboardRounded'
import LoginIcon from '@mui/icons-material/LoginRounded'
import MenuIcon from '@mui/icons-material/MenuRounded'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded'
import ConfigureIcon from '@mui/icons-material/SettingsRounded'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { alpha, styled, useTheme } from '@mui/material/styles'
import Cookies from 'js-cookie'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import AccountButton from '../components/AccountButton'
import DirectorySelect from '../components/DirectorySelect'
import Footer from '../components/Footer'
import { authRequest } from '../util/api/auth-api'
import checkToken from '../util/auth/checkToken'
import useUser from '../util/auth/useUser'
import { useTracking } from '../util/tracking'
import handleError from '../util/handleError'
import { usePylonChatSettings } from '../util/auth/usePylonChatSettings'
import { usePylonHidePoweredBy } from '../util/auth/usePylonHidePoweredBy'
import TokenRefreshManager from '../components/auth/TokenRefreshManager'
import { getCNAIUrl } from '../util/cnaiUrl'
import useStore from '../util/store/auth'
import { AdminPanelSettings } from '@mui/icons-material'

const drawerWidth = 260

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Adjust layout based on screen size
  [theme.breakpoints.up('md')]: {
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  },
  // On smaller screens, don't adjust margin regardless of drawer state
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
  },
}))

const AppBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{
  open?: boolean
}>(({ theme, open }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Adjust width and margin based on screen size
  [theme.breakpoints.up('md')]: {
    ...(open && {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  },
  // On smaller screens, always take full width
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 0,
  },
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(7), // Add top margin on mobile to avoid AppBar overlap
  },
}))

type TopBarLinkColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'

const NavLayout = () => {
  useTracking()
  usePylonChatSettings()
  usePylonHidePoweredBy()
  const theme = useTheme()
  const location = useLocation()

  const isLoggedIn = Cookies.get('expiresAt') !== undefined

  // If webView cookie is present, set webView
  const webView = Cookies.get('webview') === 'true'

  const navButtonEl = useRef<HTMLButtonElement>(null)
  const [navOpen, setNavOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [mobileView, setMobileView] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [user] = useUser()
  const { directory, directories, setDirectory } = useStore()
  const [banners, setBanners] = useState<RSiteBanner[]>([])
  const isSuperAdminViewing =
    !!user?.admin &&
    !!directory &&
    !directories.some((candidate) => candidate._id === directory._id)

  const handleCloseNavMenu = () => {
    setNavOpen(false)
  }

  const handleDrawerOpen = () => {
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }

  const createHeaderNavHandler = useCallback(
    (path: string) => (e: React.MouseEvent) => {
      e.preventDefault()
      navigate(path)
      setNavOpen(false)
    },
    [navigate]
  )

  // Define the top bar links for Help
  const topBarLinks: {
    id: string
    title: string
    icon: React.ReactNode
    url: string
    color: TopBarLinkColor
  }[] = [
    {
      id: 'nav-help',
      title: 'Help',
      icon: <QuestionMarkRoundedIcon />,
      url: getCNAIUrl('/help'),
      color: 'primary',
    },
  ]

  // Define the main navigation sections
  const sections: {
    id: string
    title: string
    icon: React.ReactNode
    href: string
    action?: (e: React.MouseEvent) => void
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
    auth?: boolean
    tag?: string
    section?: 'main' | 'auth'
    disabled?: boolean
    children?: {
      id: string
      title: string
      href: string
      action?: (e: React.MouseEvent) => void
    }[]
  }[] = [
    // Main navigation sections
    {
      id: 'nav-dashboard',
      title: 'Dashboard',
      icon: <DashboardIcon />,
      href: '/dashboards/overall-stats',
      color: 'primary',
      action: createHeaderNavHandler('/dashboards/overall-stats'),
      section: 'main',
      auth: true,
      children: [
        {
          id: 'nav-overall-stats',
          title: 'Overall Metrics',
          href: '/dashboards/overall-stats',
          action: createHeaderNavHandler('/dashboards/overall-stats'),
        },
        {
          id: 'nav-all-alerts',
          title: 'All Alerts',
          href: '/dashboards/all-alerts',
          action: createHeaderNavHandler('/dashboards/all-alerts'),
        },
      ],
    },
    // {
    //   id: 'nav-audit',
    //   title: 'Audit',
    //   icon: <ReceiptLongRoundedIcon />,
    //   href: '/dashboards/overall-stats',
    //   color: 'primary',
    //   action: createHeaderNavHandler('/dashboards/overall-stats'),
    //   section: 'main',
    //   auth: true,
    // },
    {
      id: 'nav-configure',
      title: 'Configure',
      icon: <ConfigureIcon />,
      href: '/configure',
      color: 'primary',
      action: createHeaderNavHandler('/configure'),
      section: 'main',
      auth: true,
    },

    // Auth related links
    {
      id: 'nav-login',
      title: 'Login',
      icon: <LoginIcon />,
      href: '/login',
      color: 'primary',
      auth: false,
      action: createHeaderNavHandler('/login'),
      section: 'auth',
    },
    {
      id: 'nav-create-account',
      title: 'Create Account',
      icon: <PersonAddAlt1RoundedIcon />,
      href: getCNAIUrl('/pricing-select'),
      color: 'primary',
      auth: false,
      action: (e?: React.MouseEvent) => {
        e?.preventDefault()
        globalThis.open(getCNAIUrl('/pricing-select'), '_blank')
      },
      section: 'auth',
    },
  ]

  const populateBanners = useCallback(async () => {
    try {
      const loggedIn = await checkToken()
      if (!loggedIn) return

      const { results } = await authRequest.user.banners.list({ token: '' })
      if (results.length === 0) return

      setBanners(results)
    } catch (err) {
      handleError(err)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    void populateBanners()
  }, [user, populateBanners])

  // Capture the directoryId from the URL and set the directory in the store
  useEffect(() => {
    if (!user) return

    const paramDirectoryId = searchParams.get('directoryId')
    if (!paramDirectoryId) return
    if (directories.length === 0) return

    const nextSearchParams = new URLSearchParams(searchParams)
    const matchingDirectory = directories.find((candidate) => candidate._id === paramDirectoryId)

    const syncDirectoryFromParam = async () => {
      try {
        // User is the member of the directory
        if (matchingDirectory && directory?._id !== matchingDirectory._id) {
          setDirectory(matchingDirectory)
        } else if (!matchingDirectory) {
          // User is not the member of the directory, check if the user is super admin
          if (user.admin) {
            // fetch the directory by id
            const { result } = await authRequest.user.directories.get({
              token: '',
              directoryId: paramDirectoryId,
            })
            if (result) {
              setDirectory(result)
            }
          }
        }
      } catch (err) {
        handleError(err)
      } finally {
        // Always remove directoryId to avoid retry loops on failed lookups.
        nextSearchParams.delete('directoryId')
        setSearchParams(nextSearchParams, { replace: true })
      }
    }

    void syncDirectoryFromParam()
  }, [directory?._id, directories, searchParams, setDirectory, setSearchParams, user])

  // Check if we're on a mobile device
  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 900) // 'md' breakpoint in MUI is typically 900px

      // Auto-close drawer on mobile when resizing
      if (window.innerWidth < 900) {
        setDrawerOpen(false)
      } else {
        setDrawerOpen(true)
      }
    }

    // Set initial value
    handleResize()

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isActive = (href: string) => {
    return location.pathname === href
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <TokenRefreshManager />
      <CssBaseline />

      {banners.map((banner) => (
        <Box
          key={banner._id}
          sx={{
            p: 0.5,
            px: 1,
            width: '100%',
            backgroundColor: 'secondary.main',
            position: 'fixed',
            top: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Typography variant="body1" color="secondary.contrastText" fontWeight={500}>
            {banner.text}
          </Typography>
        </Box>
      ))}

      {!webView && (
        <>
          <AppBar
            position="fixed"
            open={drawerOpen && !mobileView}
            component="nav"
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <Toolbar
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  data-qa-id="nav-menu-open-button"
                  onClick={handleDrawerOpen}
                  edge="start"
                  sx={{ mr: 2, ...(!mobileView && drawerOpen && { display: 'none' }) }}
                >
                  <MenuIcon />
                </IconButton>

                <Box
                  component="a"
                  href="/"
                  id="logo-home"
                  data-qa-id="nav-logo-link"
                  sx={{
                    display: 'flex',
                    textDecoration: 'none',
                    color: 'inherit',
                    alignItems: 'center',
                  }}
                  onClick={createHeaderNavHandler('/')}
                >
                  <img src="/logo-alt.svg" alt="Logo" width={40} height={40} />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    component="h1"
                    sx={{
                      ml: 2,
                      display: { xs: 'none', lg: 'block' }, // Hide on mobile screens
                    }}
                    fontFamily="Comfortaa, cursive"
                    lineHeight={1.1}
                  >
                    Clinically AI - Comply
                  </Typography>
                </Box>
              </Box>

              {/* Top Bar Navigation Links */}
              <Box sx={{ display: 'flex', mx: 2 }}>
                {topBarLinks.map((link) => (
                  <Button
                    key={link.id}
                    id={link.id}
                    data-qa-id={`${link.id}-link`}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color={link.color}
                    sx={{ mx: 0.5 }}
                    startIcon={link.icon}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(link.url, '_blank')
                    }}
                    variant="outlined"
                  >
                    {link.title}
                  </Button>
                ))}
              </Box>
              {!!isLoggedIn && (
                <Box sx={{ display: 'flex', ml: 'auto' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <DirectorySelect sx={{ width: 240 }} />
                    {isSuperAdminViewing && (
                      <Chip
                        label=""
                        icon={<AdminPanelSettings />}
                        color="warning"
                        size="medium"
                        variant="outlined"
                        sx={{
                          width: 34,
                          height: 34,
                          '& .MuiChip-label': { display: 'none' },
                          '& .MuiChip-icon': { margin: 0 },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <AccountButton />
                  </Box>
                </Box>
              )}
            </Toolbar>
          </AppBar>

          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.background.paper,
                [theme.breakpoints.down('md')]: {
                  zIndex: 1400,
                },
              },
            }}
            variant={mobileView ? 'temporary' : 'persistent'}
            anchor="left"
            open={drawerOpen}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
          >
            <Box
              sx={{
                mt: banners.length > 0 ? 6 : 0,
              }}
            >
              <DrawerHeader sx={{ backgroundColor: theme.palette.background.paper }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontFamily: 'Comfortaa, cursive',
                    fontWeight: 600,
                    pl: 1,
                  }}
                >
                  Navigation
                </Typography>
                <IconButton
                  onClick={handleDrawerClose}
                  data-qa-id="nav-drawer-close-button"
                  sx={{
                    // Make sure it's always visible and clickable
                    zIndex: 5,
                  }}
                >
                  {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </DrawerHeader>

              {isLoggedIn && <Divider />}

              <Box sx={{ mt: 2 }}>
                {sections
                  .filter((section) => section.section === 'main')
                  .filter((section) => {
                    if (section.auth && !isLoggedIn) return false
                    if (section.auth === false && isLoggedIn) return false
                    return true
                  })
                  .map((section, i) => {
                    const hasChildren = section.children && section.children.length > 0
                    const isExpanded = expandedSection === section.id
                    const isDashboardActive =
                      section.id === 'nav-dashboard' &&
                      (isActive(section.href) ||
                        section.children?.some((child) => isActive(child.href)))

                    // Check if any child of this section is active
                    const isAnyChildActive =
                      section.children?.some((child) => isActive(child.href)) || false

                    return (
                      <React.Fragment key={section.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            id={section.id}
                            data-qa-id={`${section.id}-link`}
                            component="a"
                            href={section.href}
                            onClick={(e) => {
                              if (hasChildren) {
                                e.preventDefault()
                                if (section.id === 'nav-dashboard') {
                                  navigate('/dashboards/overall-stats')
                                }
                                setExpandedSection(isExpanded ? null : section.id)
                              } else if (section.action) {
                                section.action(e)
                              }
                            }}
                            selected={isActive(section.href) || isDashboardActive}
                            sx={{
                              borderRadius: '0 12px 12px 0',
                              mr: 2,
                              mb: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}20`
                                    : `${theme.palette.primary.main}20`,
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}30`
                                    : `${theme.palette.primary.main}30`,
                              },
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}10`
                                    : `${theme.palette.primary.main}10`,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: (theme) =>
                                  section.color
                                    ? theme.palette[section.color].main
                                    : theme.palette.primary.main,
                                minWidth: 40,
                              }}
                            >
                              {section.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={section.title}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                color: (theme) =>
                                  section.color
                                    ? theme.palette[section.color].main
                                    : theme.palette.text.primary,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>

                        {/* Children for this section - only show if a child is active */}
                        {hasChildren && isAnyChildActive && (
                          <Box
                            sx={{
                              ml: 1.5,
                              borderLeft: '1px dashed',
                              borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                              pl: 1.5,
                              mt: 0.5,
                              mb: 1,
                            }}
                          >
                            {section.children?.map((child, j) => (
                              <ListItem key={child.id} disablePadding>
                                <ListItemButton
                                  id={child.id}
                                  data-qa-id={`${child.id}-link`}
                                  component="a"
                                  href={child.href}
                                  onClick={child.action}
                                  selected={isActive(child.href)}
                                  sx={{
                                    borderRadius: 1,
                                    mr: 2,
                                    my: 0.25,
                                    py: 0.75,
                                    minHeight: 36,
                                    '&.Mui-selected': {
                                      backgroundColor: (theme) => `${theme.palette.primary.main}15`,
                                      color: (theme) => theme.palette.primary.main,
                                    },
                                    '&.Mui-selected:hover': {
                                      backgroundColor: (theme) => `${theme.palette.primary.main}25`,
                                      color: (theme) => theme.palette.primary.main,
                                    },
                                    '&:hover': {
                                      backgroundColor: (theme) => `${theme.palette.primary.main}08`,
                                      color: (theme) => theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  <ListItemText
                                    primary={child.title}
                                    primaryTypographyProps={{
                                      fontWeight: 500,
                                      variant: 'body2',
                                      fontSize: '0.875rem',
                                    }}
                                    sx={{ ml: 0.5 }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </Box>
                        )}
                      </React.Fragment>
                    )
                  })}
              </Box>

              {/* Auth Section - Only show if relevant */}
              {!isLoggedIn && (
                <>
                  <Divider sx={{ mt: 2 }} />

                  <Typography
                    variant="subtitle2"
                    sx={{
                      px: 3,
                      pt: 2,
                      pb: 1,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Account
                  </Typography>

                  <Box>
                    {sections
                      .filter((section) => section.section === 'auth')
                      .filter((section) => {
                        if (section.auth && !isLoggedIn) return false
                        if (section.auth === false && isLoggedIn) return false
                        return true
                      })
                      .map((section, i) => (
                        <ListItem key={section.id} disablePadding>
                          <ListItemButton
                            id={section.id}
                            data-qa-id={`${section.id}-link`}
                            component="a"
                            href={section.href}
                            onClick={section.action}
                            selected={isActive(section.href)}
                            sx={{
                              borderRadius: '0 12px 12px 0',
                              mr: 2,
                              mb: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}20`
                                    : `${theme.palette.primary.main}20`,
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}30`
                                    : `${theme.palette.primary.main}30`,
                              },
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  section.color
                                    ? `${theme.palette[section.color].main}10`
                                    : `${theme.palette.primary.main}10`,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: (theme) =>
                                  section.color
                                    ? theme.palette[section.color].main
                                    : theme.palette.primary.main,
                                minWidth: 40,
                              }}
                            >
                              {section.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={section.title}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                color: (theme) =>
                                  section.color
                                    ? theme.palette[section.color].main
                                    : theme.palette.text.primary,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </Box>
                </>
              )}
            </Box>
          </Drawer>
        </>
      )}

      <Main open={drawerOpen && !webView && !mobileView} sx={{ mt: banners.length > 0 ? 8 : 0 }}>
        <DrawerHeader />
        <Outlet />
        <Container sx={{ p: 2, mt: 2 }} maxWidth="lg">
          <Footer />
        </Container>
      </Main>

      <Menu
        aria-expanded={navOpen}
        id="mobile-nav-menu"
        anchorEl={navButtonEl.current}
        open={navOpen}
        onClose={handleCloseNavMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={(theme) => ({
          '& .MuiMenu-paper': {
            backgroundColor: theme.palette.background.default,
            boxShadow: theme.shadows[24],
          },
        })}
      >
        {/* Main navigation items */}
        {sections.map((section, i) => {
          if (section.auth && !isLoggedIn) return null
          if (section.auth === false && isLoggedIn) return null

          const hasChildren = section.children && section.children.length > 0
          const isAnyChildActive = section.children?.some((child) => isActive(child.href)) || false

          return [
            <MenuItem
              key={'sm-' + section.id}
              href={section.href}
              LinkComponent={'a'}
              onClick={(e) => {
                if (hasChildren) {
                  e.preventDefault()
                  section.action?.(e)
                } else if (section.action) {
                  section.action(e)
                }
                if (!hasChildren) {
                  handleCloseNavMenu()
                }
              }}
              id={'mobile-' + section.id}
              selected={isActive(section.href)}
            >
              <Box
                sx={(theme) => ({
                  mr: 1,
                  color: section.color ? theme.palette[section.color]?.main : undefined,
                  display: 'flex',
                  alignItems: 'center',
                })}
              >
                {section.icon}
              </Box>
              <Typography>{section.title}</Typography>
            </MenuItem>,
            ...(hasChildren && isAnyChildActive
              ? (section.children ?? []).map((child, j) => (
                  <MenuItem
                    key={`${section.id}-child-${j}`}
                    href={child.href}
                    LinkComponent={'a'}
                    onClick={(e) => {
                      if (child.action) {
                        child.action(e)
                      }
                      handleCloseNavMenu()
                    }}
                    id={'mobile-' + child.id}
                    sx={{
                      pl: 4,
                      borderLeft: '1px dashed',
                      borderColor: 'divider',
                      ml: 4,
                      my: 0.5,
                      fontSize: '0.9rem',
                    }}
                    selected={isActive(child.href)}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {child.title}
                    </Typography>
                  </MenuItem>
                ))
              : []),
          ]
        })}

        {/* Top bar links */}
        {topBarLinks.map((link, i) => (
          <MenuItem
            key={'sm-' + link.id}
            component="a"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault()
              window.open(link.url, '_blank')
              handleCloseNavMenu()
            }}
            id={'mobile-' + link.id}
          >
            <Box
              sx={(theme) => ({
                mr: 1,
                color: theme.palette[link.color].main,
                display: 'flex',
                alignItems: 'center',
              })}
            >
              {link.icon}
            </Box>
            <Typography>{link.title}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Toaster
        gutter={8}
        toastOptions={{
          className: '',
          duration: 5000,
          style: {
            borderRadius: 12,
          },
        }}
      />
    </Box>
  )
}

export default NavLayout
